import React, { useCallback, useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { useNavigate, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import FormHelperText from "@mui/material/FormHelperText";
import List from "@mui/material/List";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useApp } from "../../design/AppContext";
import { apiGet, apiPostForm } from "../../utils/api";
import { apiErrorMessage } from "../../utils/apiError";
import { hasPermission } from "../../utils/permission";
import { jwtEncode } from "../../utils/jwt";
import { IMPORT_TIMEOUT_MS } from "../../components/task/TaskImportDialog";
import {
  IMPORT_COLUMNS,
  MAX_IMPORT_ROWS,
  parseSheet,
  mergeServerResults,
  rowIssues,
  rowWarnings,
  summarize,
  templateHeaders,
  templateSample,
  templateNotes,
} from "../../utils/taskImport";
import { MdCheck, MdErrorOutline, MdRemove, MdWarningAmber } from "react-icons/md";
import { Banner, DataRow, EmptyState, StatusChip } from "../../components/mui";
import { InlineLoading } from "../../components/loading";

/**
 * 從 Excel 匯入工項（設計稿 #screen-import，el-pm-template_4.html L2537-L2567）
 *
 * 為什麼從彈窗改成整頁：
 *   · 舊的 TaskImportDialog 是一個 1080px 寬、內含 13 欄表格的對話框。
 *     那張表在大字模式（--t-body 21px）下光標題列就超過視窗寬度，
 *     使用者要橫向捲動才看得到「檢查結果」那一欄 —— 而那正是他唯一要看的東西。
 *     整頁之後每一列改成一段可換行的清單列，大字模式不會超出畫面。
 *   · 匯入是一件有後果的事（一次寫入上百筆），值得一個可以貼連結、可以按上一頁的網址。
 *
 * 動作的輕重也照設計稿調過：入口是專案詳情的次要按鈕（不與「新增工項」同權重），
 * 而這一頁的主鈕是「看看會匯入什麼」而不是直接匯入 —— 先看過再寫入。
 *
 * 解析與驗證完全沿用 utils/taskImport.js 的純函式（有測試，一個字都沒改），
 * 端點與逾時沿用 TaskImportDialog 的那一份（IMPORT_TIMEOUT_MS 由它匯出，只有一份）。
 *
 * 版面規則：外層不另外包頁面容器，Layout.js 的內容區本身已有內距與寬度上限。
 */

/*
 * SheetJS（xlsx）。動態 import 的理由同 TaskImportDialog：
 * 壓縮後仍有 140KB 以上，靜態 import 會被打進 main bundle，
 * 變成每一個開系統的人都要為偶爾才用一次的功能多下載一份。
 */
const loadXlsx = () => import("xlsx");

/** 上面那一排步驟：選檔 → 預覽。預覽之後才有「開始匯入」 */
const STEP_PICK = "pick";
const STEP_PREVIEW = "preview";

/**
 * 預覽列要在 meta 裡講的欄位。
 * 就是 IMPORT_COLUMNS 去掉標題（標題已經是列的主標），
 * 一欄都沒有少 —— 舊表格看得到的值，這裡同樣看得到，只是改成一列一段。
 */
const META_COLUMNS = IMPORT_COLUMNS.filter((c) => c.key !== "title");

function ProjectImport() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useApp();
  const projectId = Number(id) || 0;

  /* 匯入寫的是工項，所以看的是 task 權限（同 Project/Detail 的匯入入口） */
  const canImportTask = useMemo(() => hasPermission("task"), []);

  const [step, setStep] = useState(STEP_PICK);
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState([]);
  /** 整張表不能用的原因（找不到標題列、缺必填欄、超過列數上限…） */
  const [fatal, setFatal] = useState("");
  const [unknownHeaders, setUnknownHeaders] = useState([]);
  const [busy, setBusy] = useState("");
  /** 後端回來的總計（試跑或匯入） */
  const [result, setResult] = useState(null);
  /**
   * 匯入的請求逾時，結果未知（見 send 的 catch）。
   * 伺服器那邊很可能已經 commit，所以這之後絕對不能再讓人按「開始匯入」——
   * 一按就是第二次完整寫入，最多 200 筆重複工項，而且沒有批次刪除可以收拾。
   */
  const [outcomeUnknown, setOutcomeUnknown] = useState(false);

  /* 這個專案的名稱與可比對的名單（里程碑／成員／既有工項代碼） */
  const [projectName, setProjectName] = useState("");
  const [phaseNames, setPhaseNames] = useState([]);
  const [people, setPeople] = useState([]);
  const [issueKeys, setIssueKeys] = useState([]);

  /*
   * 名單是「前端順手提醒」用的，拿不到就整個不檢查、交給後端（taskImport.js 的分工說明）。
   * 所以這裡每一支都各自 catch，任何一支掛掉都不該擋住匯入本身。
   */
  useEffect(() => {
    if (!projectId) return undefined;
    let alive = true;

    apiGet("/Project", { Data: jwtEncode({ ProjectId: projectId }) })
      .then((d) => {
        if (!alive) return;
        setProjectName(String(_.get(d, "name", "") || ""));
        setPhaseNames((_.get(d, "phases", []) || []).map((p) => _.get(p, "name", "")).filter(Boolean));
        setPeople(
          (_.get(d, "members", []) || []).map((m) => ({
            name: _.get(m, "name", "") || "",
            account: _.get(m, "account", "") || "",
          }))
        );
      })
      .catch(() => {});

    apiGet(`/Task/List/${projectId}`, {
      Data: jwtEncode({ Page: 0, PageCount: 0, OrderKey: "id", OrderDirect: "ASC" }),
    })
      .then((d) => {
        if (!alive) return;
        const arr = Array.isArray(d) ? d : _.get(d, "data_list", []) || [];
        /*
         * 只收真正的工項代碼（issue_key），不要用 normalizeTask 的 `#id` 退路：
         * 那個退路會讓名單看起來非空，於是每一列的「父工項代碼」都被判成找不到，
         * 使用者會收到一整排其實不成立的提醒。
         */
        setIssueKeys(arr.map((t) => _.get(t, "issue_key", _.get(t, "issueKey", ""))).filter(Boolean));
      })
      .catch(() => {});

    return () => {
      alive = false;
    };
  }, [projectId]);

  const goProject = () => navigate(projectId ? `/Project/Detail/${projectId}` : "/Project/List");

  /* ---------- 選檔與解析 ---------- */

  const readFile = useCallback(
    async (file) => {
      if (!file) return;
      setBusy("parse");
      setResult(null);
      setOutcomeUnknown(false);
      try {
        const XLSX = await loadXlsx();
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf, { type: "array" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        if (!sheet) {
          setRows([]);
          setFatal("此檔案中沒有任何工作表");
          return;
        }
        /*
         * blankrows: true 是必要的 —— 空白列若在這裡就被丟掉，後面每一列的列號都會往前位移，
         * 錯誤訊息講的「第 12 列」就會指到 Excel 上的另一列。空白列的略過交給 parseSheet。
         */
        const aoa = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, blankrows: true, defval: "" });
        /*
         * sheet_to_json 是從工作表 !ref 的起點開始輸出，不是從第 1 列：dimension 若是 A3:L20，
         * aoa 的索引 0 其實是 Excel 的第 3 列。不把位移算進去，錯誤訊息會指錯列。
         */
        const range = sheet["!ref"] ? XLSX.utils.decode_range(sheet["!ref"]) : null;
        const originRowNo = range && Number.isFinite(range.s.r) ? range.s.r + 1 : 1;
        const parsed = parseSheet(aoa, { phaseNames, people, issueKeys }, originRowNo);
        if (parsed.error) {
          setRows([]);
          setFatal(parsed.error);
          setUnknownHeaders([]);
          return;
        }
        setFatal("");
        setRows(parsed.rows);
        setUnknownHeaders(parsed.unknownHeaders || []);
      } catch (e) {
        setRows([]);
        setFatal(`讀取失敗：${e && e.message ? e.message : "檔案格式不正確"}。請確認是 .xlsx / .xls / .csv 檔。`);
      } finally {
        setBusy("");
      }
    },
    [phaseNames, people, issueKeys]
  );

  const onPick = (e) => {
    const file = e.target.files && e.target.files[0];
    setFileName(file ? file.name : "");
    readFile(file);
    // 同一個檔案改完再選一次也要能觸發 change
    e.target.value = "";
  };

  /** 下載範本：第一列欄位標題、第二列一筆填好的範例，另附「欄位說明」工作表 */
  const downloadTemplate = async () => {
    try {
      const XLSX = await loadXlsx();
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([templateHeaders(), templateSample()]);
      ws["!cols"] = templateHeaders().map((h) => ({ wch: Math.max(12, h.length * 2 + 6) }));
      XLSX.utils.book_append_sheet(wb, ws, "工項");

      const notes = XLSX.utils.aoa_to_sheet(templateNotes());
      notes["!cols"] = [{ wch: 14 }, { wch: 8 }, { wch: 78 }];
      XLSX.utils.book_append_sheet(wb, notes, "欄位說明");

      XLSX.writeFile(wb, "工項匯入範本.xlsx");
    } catch (e) {
      toast("範例檔下載失敗，請稍後再試", "error");
    }
  };

  /* ---------- 送出 ---------- */

  const send = async (dryRun) => {
    if (!rows.length || !projectId) return;
    setBusy(dryRun ? "dry" : "import");
    try {
      const data = await apiPostForm(
        "/Task/Import",
        {
          Data: jwtEncode({
            ProjectId: projectId,
            DryRun: !!dryRun,
            Rows: rows.map((r) => r.payload),
          }),
        },
        { timeout: IMPORT_TIMEOUT_MS }
      );
      setRows((s) => mergeServerResults(s, data && data.results));
      setResult(data || null);

      if (!dryRun && data && data.committed) {
        toast(data.message || "已匯入工項");
      } else if (data && data.message) {
        toast(data.message, data.failed > 0 ? "error" : undefined);
      }
    } catch (e) {
      /*
       * 逾時必須跟一般失敗分開講。axios 放棄之後伺服器那邊還在跑，而且很可能照樣 commit，
       * 所以這時候說「匯入失敗」是一句會害人的話 —— 使用者照著重匯一次就是整批重複。
       * 講「結果未知、先去工項列表確認」才是他當下真正做得到、也真正該做的事。
       */
      const timedOut = !!e && (e.code === "ECONNABORTED" || /timeout/i.test(String(e.message || "")));
      if (timedOut && !dryRun) {
        // 只靠文字擋不住手比腦快的人：底下的按鈕一起收掉，才不會一鍵就是整批重複
        setOutcomeUnknown(true);
        toast("匯入結果未知，請先至專案確認工項是否已建立，再決定是否重新匯入", "error");
      } else {
        toast(apiErrorMessage(e, dryRun ? "檢查失敗" : "匯入失敗"), "error");
      }
    } finally {
      setBusy("");
    }
  };

  /* ---------- 衍生狀態 ---------- */

  const sum = summarize(rows);
  const committed = !!(result && result.committed);
  /*
   * 逾時之後（outcomeUnknown）連「送到伺服器再檢查」都停掉：那時候檢查只會回
   * 「全部通過驗證，可以開始匯入」，跟畫面上那段「可能已經寫入完成」正好相反，
   * 等於親手把人推回去按匯入。這個狀態只有一條出路 —— 回專案確認。
   */
  const canDry = rows.length > 0 && !busy && !committed && !outcomeUnknown;
  const canImport = canDry && sum.bad === 0;
  const canPreview = rows.length > 0 && !busy && !fatal;

  if (!projectId) {
    return (
      <EmptyState title="找不到此專案" text="請由專案頁的「從 Excel 匯入工項」進入，網址須包含專案編號。" />
    );
  }

  if (!canImportTask) {
    return (
      <EmptyState title="您沒有建立工項的權限" text="匯入將直接建立工項，需具備「工項」權限。請聯絡系統管理員開通。" />
    );
  }

  /* ---------- 一列預覽 ---------- */

  const previewRow = (row) => {
    const issues = rowIssues(row);
    const warns = rowWarnings(row);
    const bad = issues.length > 0;
    const metas = META_COLUMNS.map((c) => {
      const v = String(row.values[c.key] ?? "").trim();
      return v ? `${c.label}：${v}` : "";
    }).filter(Boolean);

    const tag = bad
      ? { kind: "attention", mark: <MdWarningAmber />, text: "不能匯入" }
      : row.issueKey
      ? { kind: "done", mark: <MdCheck />, text: `已建立 ${row.issueKey}` }
      : warns.length
      ? { kind: "warn", mark: <MdErrorOutline />, text: "請確認" }
      : { kind: "closed", mark: <MdRemove />, text: "可匯入" };

    /* 有問題的列左邊畫一條色帶（DataRow 的 tone）；預覽列只是資料，不可點 */

    return (
      <DataRow
        key={row.rowNo}
        component="li"
        tone={bad ? "attention" : warns.length ? "warn" : ""}
        title={
          <>
            第 {row.rowNo} 列　{row.values.title || "（未填寫工項名稱）"}
          </>
        }
        meta={
          <>
            {/*
              問題與提醒寫在 meta 的最前面，而不是另外做一欄「檢查結果」——
              那一欄在大字模式下會被擠出畫面，而它正是唯一要看的東西。
            */}
            {bad && <span>{issues.join("；")}</span>}
            {!bad && warns.length > 0 && <span>{warns.join("；")}</span>}
            {metas.map((m) => (
              /* 說明欄可以很長，超出就截斷、完整內容留在 title；長度用 ch 而不是 px，
                 大字模式跟著字一起放大 */
              <Box
                component="span"
                key={m}
                title={m}
                sx={{ maxWidth: "40ch", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              >
                {m}
              </Box>
            ))}
          </>
        }
        side={
          <StatusChip kind={tag.kind} mark={tag.mark}>
            {tag.text}
          </StatusChip>
        }
      />
    );
  };

  /* ---------- 畫面 ---------- */

  const inline = { flexWrap: "wrap", alignItems: "center" };

  return (
    <>
      <Typography variant="h1" component="h1">
        從 Excel 匯入工項
      </Typography>
      <Typography sx={{ mt: 1.5 }}>
        {/* 專案名稱還沒回來（或拿不到）就不寫，不要留一個「· 」的空殼 */}
        {projectName ? `${projectName} · ` : ""}
        一次匯入整張工作表的工項。如不確定格式，請先下載範例檔。
      </Typography>

      {/* 680px 是設計稿 L2542 寫死在卡片上的值 */}
      <Card component="section" sx={{ mt: 3, maxWidth: 680 }}>
        <CardContent>
          {step === STEP_PICK ? (
            <Stack spacing={2}>
              <div>
                <Button type="button" variant="outlined" onClick={downloadTemplate}>
                  下載 Excel 範例檔
                </Button>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  範例檔第一列為欄位標題、第二列為填寫範例；另一張「欄位說明」工作表列出各欄可填寫的內容。
                </Typography>
              </div>

              <div>
                <Typography component="label" htmlFor="imp" variant="subtitle1" sx={{ display: "block" }}>
                  選擇檔案
                </Typography>
                {/* TextField type="file" 底下仍是原生檔案輸入：onPick 照樣拿 e.target.files、也照樣能清 value */}
                <TextField
                  id="imp"
                  type="file"
                  fullWidth
                  onChange={onPick}
                  slotProps={{ htmlInput: { accept: ".xlsx,.xls,.csv", "aria-describedby": "imp-help" } }}
                  sx={{ mt: 1 }}
                />
                <FormHelperText id="imp-help" sx={{ mx: 0, mt: 1 }}>
                  支援 .xlsx、.xls 與 .csv，一次最多 {MAX_IMPORT_ROWS} 列。
                  CSV 請儲存為 UTF-8 編碼，否則中文會出現亂碼；第一列須為欄位標題，欄位順序可自行調整。
                </FormHelperText>
              </div>

              {busy === "parse" && <InlineLoading text="檔案讀取中…" />}

              {/* 整張表不能用：這是擋住全部的錯誤，寫成 attention 提示條 */}
              {!!fatal && (
                <Banner kind="attention" icon={<MdWarningAmber />} role="alert">
                  <strong>此檔案無法匯入　</strong>
                  {fatal}
                </Banner>
              )}

              {!fatal && rows.length > 0 && (
                <Typography>
                  <Typography component="span" sx={{ fontWeight: 600 }}>
                    {fileName}
                  </Typography>
                  　共讀取 {rows.length} 列。
                </Typography>
              )}

              {/* 匯入前一定先預覽，這是這一頁對使用者的承諾，寫成橫幅而不是灰字小字（硬規則 4） */}
              <Banner kind="warn" icon={<MdErrorOutline />}>
                <strong>匯入前將先顯示預覽　</strong>
                系統會列出將建立的工項與有問題的列，經您確認後才會實際建立。
              </Banner>

              <Stack direction="row" spacing={1.5} useFlexGap sx={inline}>
                {/* 主鈕是「看看會匯入什麼」而不是直接匯入（設計稿 L2562） */}
                <Button
                  type="button"
                  variant="contained"
                  disabled={!canPreview}
                  onClick={() => setStep(STEP_PREVIEW)}
                >
                  預覽匯入內容
                </Button>
                <Button type="button" variant="text" onClick={goProject}>
                  取消
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Typography>
                <Typography component="span" sx={{ fontWeight: 600 }}>
                  {fileName}
                </Typography>
                　共 {sum.total} 列，其中 {sum.ok} 列可匯入。
              </Typography>

              {/* 整批不能匯入的原因；有一列不行就整批不寫（後端是全有全無的交易） */}
              {sum.bad > 0 && !committed && (
                <Banner kind="attention" icon={<MdWarningAmber />} role="alert">
                  <strong>有 {sum.bad} 列無法匯入　</strong>
                  原因標示於下方紅線標記的列。請於 Excel 修正後重新選擇檔案；只要有任一列無法匯入，整批皆不會寫入。
                </Banner>
              )}

              {sum.warn > 0 && (
                <Banner kind="warn" icon={<MdErrorOutline />}>
                  <strong>有 {sum.warn} 列需要確認　</strong>
                  這些列可以匯入，但里程碑、指派人或父工項在此專案中找不到對應項目。最終結果以伺服器檢查為準。
                </Banner>
              )}

              {unknownHeaders.length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  已略過無法辨識的欄位：{unknownHeaders.join("、")}
                </Typography>
              )}

              {/* 逾時：結果未知。這是這一頁最重要的一句話，一定要是橫幅 */}
              {outcomeUnknown && (
                <Banner kind="attention" icon={<MdWarningAmber />} role="alert">
                  <strong>匯入結果未知　</strong>
                  請求逾時中斷，但伺服器可能已完成寫入。請先至專案確認工項是否已建立，
                  再決定是否重新匯入；直接重新匯入可能造成整批重複。
                </Banner>
              )}

              {/*
                後端回來的總結（檢查或匯入）。
                失敗筆數 > 0 才用警示色 —— 「檢查完成，全部可以匯入」是一句好消息，
                用琥珀色講會讓人以為還有事要處理。
              */}
              {!!(result && result.message) && !outcomeUnknown && (
                <Banner
                  kind={result.failed > 0 ? "attention" : "info"}
                  icon={result.failed > 0 ? <MdWarningAmber /> : <MdCheck />}
                  role="status"
                >
                  {result.message}
                </Banner>
              )}

              {/* 還沒寫入時先講清楚按下去會發生什麼事 */}
              {!committed && !outcomeUnknown && sum.bad === 0 && (
                <Banner kind="warn" icon={<MdErrorOutline />}>
                  <strong>按下「開始匯入」將實際建立工項　</strong>
                  {sum.ok} 件將一次寫入此專案；須全部成功才會生效，任一列失敗將整批復原。
                </Banner>
              )}

              <Stack direction="row" spacing={1.5} useFlexGap sx={inline}>
                {committed || outcomeUnknown ? (
                  <Button type="button" variant="contained" onClick={goProject}>
                    返回專案
                  </Button>
                ) : (
                  <>
                    <Button
                      type="button"
                      variant="contained"
                      disabled={!canImport}
                      loading={busy === "import"}
                      loadingPosition="start"
                      onClick={() => send(false)}
                    >
                      {busy === "import" ? "匯入中…" : `開始匯入${sum.ok ? ` ${sum.ok} 件` : ""}`}
                    </Button>
                    {/* 後端用同一套規則再驗一次但一個字都不寫；里程碑、指派人這種要查資料的只有它算數 */}
                    <Button
                      type="button"
                      variant="outlined"
                      disabled={!canDry}
                      loading={busy === "dry"}
                      loadingPosition="start"
                      onClick={() => send(true)}
                    >
                      {busy === "dry" ? "檢查中…" : "伺服器檢查"}
                    </Button>
                    <Button
                      type="button"
                      variant="outlined"
                      disabled={!!busy}
                      onClick={() => {
                        setStep(STEP_PICK);
                        setResult(null);
                      }}
                    >
                      重新選擇檔案
                    </Button>
                    <Button type="button" variant="text" disabled={!!busy} onClick={goProject}>
                      取消
                    </Button>
                  </>
                )}
              </Stack>
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* 逐列預覽。每一列一個 DataRow（li），內容可換行，大字模式不會像表格那樣超寬 */}
      {step === STEP_PREVIEW && rows.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <List disablePadding>{rows.map(previewRow)}</List>
        </Card>
      )}
    </>
  );
}

export default ProjectImport;
